import React, { useEffect, useState, useCallback, useMemo, useContext } from 'react';
import { Typography, Table, Row, Col, Select, DatePicker, Button, Tooltip, Card, Statistic, Drawer, Tag, Popover, Segmented, Space } from 'antd';
import { SearchOutlined, InfoCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';

const { RangePicker } = DatePicker;

// Raw component fields aggregated for the Summary view. Every derived column
// (Order Fill %, Grad %, FTE Above Target, Total Terms) is computed from these,
// so summing them per team lets the existing renderers produce correct ratios
// rather than an average of per-cohort averages. Module scope keeps the array
// identity stable across renders.
const SUM_FIELDS = [
  'OrderCount', 'GraduationTargetFTE', 'OrderFillFTENumerator', 'GradFTE',
  'AcceptedOffer', 'RescindedOffer', 'NoShow', 'OrientationFallout',
  'InTraining', 'FormerTrainee', 'InProduction', 'FormerEmployee',
  'InterTransfer', 'StillEmployed', 'InFlightFTE',
  'Grads60Eligible', 'Grads60Retained',
  'Terms0to6', 'Terms7to13', 'Terms14to20', 'Terms21to60', 'Terms61to180',
  'Terms181plus',
];

const COST_SUM_FIELDS = [
  'NonGradCount', 'TotalHours', 'NonGradsWages', 'NonGradsHiringCosts',
  'NonGradsTotalCost',
];

const RETURN_SUM_FIELDS = [
  'TotalInClass', 'StartedOrientation', 'GradCount', 'TrainingHours',
  'BillableHours', 'TrainingCost', 'BillableRevenue', 'NetReturn',
];

// Per-cohort rates, not quantities. Several renderers gate a money column on
// these being present (TrainingWage == null renders a dash), so a combined row
// has to carry one through or its costs would blank out. They are never summed
// -- adding two hourly rates together would be meaningless.
const COST_RATE_FIELDS   = ['TrainingWage', 'HireCost'];
const RETURN_RATE_FIELDS = ['TrainingWage', 'BillableRate'];

/**
 * Combine cohort rows into one row per team.
 *
 * Quantities are summed so the existing renderers recompute ratios from
 * combined components rather than averaging per-cohort percentages. Rates are
 * carried through if any cohort has one. Fields that describe a single cohort
 * and cannot be combined (BillableAfterDays, JobName) are deliberately left
 * unset so they render as a dash rather than showing one cohort's value as if
 * it applied to the whole team.
 */
function groupRowsByTeam(rows, sumFields, rateFields = []) {
  const map = {};
  for (const r of rows) {
    const key = r.TeamName ?? '—';
    if (!map[key]) {
      map[key] = { TeamName: r.TeamName, JobName: null, StartDate: null, _starts: new Set() };
      sumFields.forEach(f => { map[key][f] = 0; });
      rateFields.forEach(f => { map[key][f] = null; });
    }
    const t = map[key];
    // Distinct start dates, not row count: a team running two jobs from one
    // start date is one class, but two rows.
    if (r.StartDate) t._starts.add(r.StartDate);
    sumFields.forEach(f => { t[f] += Number(r[f] ?? 0); });
    rateFields.forEach(f => { if (t[f] == null && r[f] != null) t[f] = r[f]; });
    // Earliest cohort start, so date-gated renderers treat a team with any
    // started cohort as started rather than suppressing its figures.
    if (r.StartDate && (!t.StartDate || r.StartDate < t.StartDate)) t.StartDate = r.StartDate;
  }
  return Object.values(map)
    .map(({ _starts, ...t }) => ({ ...t, Classes: _starts.size }))
    .sort((a, b) => (a.TeamName ?? '').localeCompare(b.TeamName ?? ''));
}

// Columns that describe a single cohort and are dropped when combining.
// BillableAfterDays is a per-cohort day number ("Day 14") with no meaningful
// combination across cohorts, so it is hidden rather than shown permanently
// blank.
const isCohortColumn = c =>
  c.key === 'StartDate' || c.key === 'JobName' || c.key === 'BillableAfterDays';

/**
 * FTE grads above target, holding out seats still in flight.
 *
 * A class with people still In Training or on an Accepted Offer has not
 * finished, so those seats have neither graduated nor washed out. Measuring
 * against the full target would count them as a shortfall and make every
 * in-progress class look behind. The target is reduced by the in-flight FTE
 * instead, which is the same as crediting them optimistically.
 */
function gradsAboveTarget(r) {
  const target = Number(r?.GraduationTargetFTE ?? 0);
  if (!target) return null;
  const inFlight = Number(r?.InFlightFTE ?? 0);
  return (r?.GradFTE ?? 0) - Math.max(0, target - inFlight);
}

/**
 * Aggregate above-target: the sum of the per-cohort figures, never a formula
 * over summed columns. The per-cohort calc floors the in-flight credit at the
 * remaining target, so summing the components first would let one class's
 * surplus in-flight seats offset another class's real shortfall and the Total
 * row would disagree with the column above it.
 */
function sumGradsAboveTarget(list) {
  return (list || []).reduce((acc, r) => acc + (gradsAboveTarget(r) ?? 0), 0);
}

// Internal retention goal: at least this share of grads still employed this
// many days after graduating.
const RETENTION_DAYS     = 60;
const RETENTION_GOAL_PCT = 80;

/** Retained share of grads old enough to have reached the mark, or null. */
function retentionPct(r) {
  const elig = r?.Grads60Eligible ?? 0;
  if (!elig) return null;
  return (r.Grads60Retained ?? 0) / elig * 100;
}

function RetentionCell({ row }) {
  const pct  = retentionPct(row);
  const elig = row?.Grads60Eligible ?? 0;
  if (pct == null) {
    // No grad is 60 days past graduation yet, which is not the same as 0%
    return (
      <Tooltip title={`No grads have reached ${RETENTION_DAYS} days since graduation yet`}>
        <span style={{ color: '#bbb' }}>—</span>
      </Tooltip>
    );
  }
  const met = pct >= RETENTION_GOAL_PCT;
  return (
    <Tooltip title={`${row.Grads60Retained} of ${elig} grads past ${RETENTION_DAYS} days`}>
      <strong style={{ color: met ? '#52c41a' : '#ff4d4f' }}>{pct.toFixed(1)}%</strong>
    </Tooltip>
  );
}

const CLASSES_COL = {
  title: '# Classes', dataIndex: 'Classes', key: 'Classes', width: 100, align: 'right',
  sorter: (a, b) => (a.Classes ?? 0) - (b.Classes ?? 0),
};

/** Drop the per-cohort columns and slot # Classes in right after Team. */
function summaryColumns(cols) {
  const out = cols.filter(c => !isCohortColumn(c));
  const teamIdx = out.findIndex(c => c.key === 'TeamName');
  out.splice(teamIdx >= 0 ? teamIdx + 1 : 0, 0, CLASSES_COL);
  return out;
}

function fmtPct(grads, formerTrainee) {
  const denom = grads + formerTrainee;
  if (!denom) return '—';
  return `${(grads / denom * 100).toFixed(1)}%`;
}

function fmtFillPct(numerator, orderCount) {
  if (!orderCount) return '—';
  return `${(numerator / orderCount * 100).toFixed(1)}%`;
}

function orderFillNumerator(r) {
  return r.OrientationFallout + r.InTraining + r.FormerTrainee + r.FormerEmployee + r.InProduction + r.InterTransfer;
}

function fmtCurrency(v) {
  if (v == null) return '—';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function RecruitmentStatsPage() {
  const currentUser       = useContext(UserContext);
  const isAdmin           = currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin';
  const canViewFinancials = (currentUser?.CanViewFinancials ?? 0);
  // A client's own login sees no wages or revenue: the two cost sections are
  // left out (and auth.py refuses them their endpoints).
  const isClient          = currentUser?.Role === 'client';

  const [teams,   setTeams]   = useState([]);
  const [jobs,    setJobs]    = useState([]);
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [costRows,     setCostRows]     = useState([]);
  const [costLoading,  setCostLoading]  = useState(false);
  const [returnRows,   setReturnRows]   = useState([]);
  // 'detail' = one row per cohort, 'summary' = one row per team
  const [detailView,   setDetailView]   = useState('detail');
  const [costView,     setCostView]     = useState('summary');
  const [returnView,   setReturnView]   = useState('summary');
  const [returnLoading, setReturnLoading] = useState(false);

  const [termDrawer,        setTermDrawer]        = useState(null); // {bucket, label}
  const [termDrawerRows,    setTermDrawerRows]    = useState([]);
  const [termDrawerLoading, setTermDrawerLoading] = useState(false);

  const [dateRange, setDateRange] = useState([null, null]);
  const [teamId,    setTeamId]    = useState(null);
  const [jobId,     setJobId]     = useState(null);

  useEffect(() => {
    api.get('/api/teams?my_teams=1&no_client_users=1').then((r) => setTeams(r.data)).catch(() => {});
    api.get('/api/jobs').then((r) => {
      setJobs(r.data);
      // Matched by name rather than a hardcoded ID so it survives differing
      // IDs between environments; trimmed and case-insensitive so a stray
      // space or capitalisation does not silently drop the default.
      const agent = r.data.find(
        (j) => (j.JobName || '').trim().toLowerCase() === 'agent');
      if (agent) setJobId(agent.JobID);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const params = {};
    if (dateRange[0]) params.start_from = dateRange[0].format('YYYY-MM-DD');
    if (dateRange[1]) params.start_to   = dateRange[1].format('YYYY-MM-DD');
    if (teamId)       params.team_id    = teamId;
    if (jobId)        params.job_id     = jobId;

    setLoading(true);
    api.get('/api/census/recruitment-stats', { params })
      .then((r) => setRows(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));

    if (isClient) return;

    setCostLoading(true);
    api.get('/api/census/training-costs', { params })
      .then((r) => setCostRows(r.data))
      .catch(() => {})
      .finally(() => setCostLoading(false));

    setReturnLoading(true);
    api.get('/api/census/training-return', { params })
      .then((r) => setReturnRows(r.data))
      .catch(() => {})
      .finally(() => setReturnLoading(false));
  }, [dateRange, teamId, jobId, isClient]);

  useEffect(() => { load(); }, [load]);

  const openTermDrawer = useCallback((bucket, label) => {
    setTermDrawer({ bucket, label });
    setTermDrawerRows([]);
    setTermDrawerLoading(true);
    const params = { bucket };
    if (dateRange[0]) params.start_from = dateRange[0].format('YYYY-MM-DD');
    if (dateRange[1]) params.start_to   = dateRange[1].format('YYYY-MM-DD');
    if (teamId)       params.team_id    = teamId;
    if (jobId)        params.job_id     = jobId;
    api.get('/api/census/term-detail', { params })
      .then((r) => setTermDrawerRows(r.data))
      .catch(() => {})
      .finally(() => setTermDrawerLoading(false));
  }, [dateRange, teamId, jobId]);

  // ── Pivot: Team+Job rows × Month columns ────────────────────────────────────
  const pivot = useMemo(() => {
    if (!rows.length) return { months: [], data: [], monthTotals: {} };

    const monthSet = new Set(rows.map((r) => r.StartDate.substring(0, 7)));
    const months = [...monthSet].sort();

    const comboMap = new Map();
    for (const r of rows) {
      const key = `${r.TeamName}|||${r.JobName ?? ''}`;
      if (!comboMap.has(key)) comboMap.set(key, { TeamName: r.TeamName, JobName: r.JobName });
    }
    const combos = [...comboMap.entries()].sort(([a], [b]) => a.localeCompare(b));

    const cellMap = {};
    for (const r of rows) {
      const comboKey = `${r.TeamName}|||${r.JobName ?? ''}`;
      const monthKey = r.StartDate.substring(0, 7);
      if (!cellMap[comboKey]) cellMap[comboKey] = {};
      if (!cellMap[comboKey][monthKey]) cellMap[comboKey][monthKey] = { orderCount: 0, gradTargetFTE: 0, acceptedOffer: 0, noShow: 0, orientationFallout: 0, inTraining: 0, grads: 0, gradFTE: 0, formerTrainee: 0, stillEmployed: 0, t0: 0, t7: 0, t14: 0, t21: 0, t61: 0, t181: 0 };
      const grads = r.InProduction + r.FormerEmployee + r.InterTransfer;
      cellMap[comboKey][monthKey].orderCount         += Number(r.OrderCount          ?? 0);
      cellMap[comboKey][monthKey].gradTargetFTE      += Number(r.GraduationTargetFTE ?? 0);
      cellMap[comboKey][monthKey].acceptedOffer      += r.AcceptedOffer      ?? 0;
      cellMap[comboKey][monthKey].noShow             += r.NoShow             ?? 0;
      cellMap[comboKey][monthKey].orientationFallout += r.OrientationFallout ?? 0;
      cellMap[comboKey][monthKey].inTraining         += r.InTraining         ?? 0;
      cellMap[comboKey][monthKey].grads              += grads;
      cellMap[comboKey][monthKey].gradFTE            += r.GradFTE            ?? 0;
      cellMap[comboKey][monthKey].formerTrainee      += r.FormerTrainee;
      cellMap[comboKey][monthKey].stillEmployed      += r.StillEmployed ?? 0;
      cellMap[comboKey][monthKey].t0                 += r.Terms0to6     ?? 0;
      cellMap[comboKey][monthKey].t7                 += r.Terms7to13    ?? 0;
      cellMap[comboKey][monthKey].t14                += r.Terms14to20   ?? 0;
      cellMap[comboKey][monthKey].t21                += r.Terms21to60   ?? 0;
      cellMap[comboKey][monthKey].t61                += r.Terms61to180  ?? 0;
      cellMap[comboKey][monthKey].t181               += r.Terms181plus  ?? 0;
    }

    const data = combos.map(([key, { TeamName, JobName }]) => {
      const row = { key, TeamName, JobName };
      const total = { orderCount: 0, gradTargetFTE: 0, acceptedOffer: 0, noShow: 0, orientationFallout: 0, inTraining: 0, grads: 0, gradFTE: 0, formerTrainee: 0, stillEmployed: 0, t0: 0, t7: 0, t14: 0, t21: 0, t61: 0, t181: 0 };
      for (const m of months) {
        const cell = cellMap[key]?.[m] ?? null;
        row[`m_${m}`] = cell;
        if (cell) {
          total.orderCount         += cell.orderCount;
          total.gradTargetFTE      += cell.gradTargetFTE;
          total.acceptedOffer      += cell.acceptedOffer;
          total.noShow             += cell.noShow;
          total.orientationFallout += cell.orientationFallout;
          total.inTraining         += cell.inTraining;
          total.grads              += cell.grads;
          total.gradFTE            += cell.gradFTE;
          total.formerTrainee      += cell.formerTrainee;
          total.stillEmployed      += cell.stillEmployed;
          total.t0                 += cell.t0;
          total.t7                 += cell.t7;
          total.t14                += cell.t14;
          total.t21                += cell.t21;
          total.t61                += cell.t61;
          total.t181               += cell.t181;
        }
      }
      row._total = total;
      return row;
    });

    const monthTotals = {};
    for (const m of months) {
      monthTotals[m] = { orderCount: 0, gradTargetFTE: 0, orientationFallout: 0, inTraining: 0, grads: 0, formerTrainee: 0 };
      for (const [key] of combos) {
        if (cellMap[key]?.[m]) {
          monthTotals[m].orderCount         += cellMap[key][m].orderCount;
          monthTotals[m].gradTargetFTE      += cellMap[key][m].gradTargetFTE;
          monthTotals[m].orientationFallout += cellMap[key][m].orientationFallout;
          monthTotals[m].inTraining         += cellMap[key][m].inTraining;
          monthTotals[m].grads              += cellMap[key][m].grads;
          monthTotals[m].formerTrainee      += cellMap[key][m].formerTrainee;
        }
      }
    }

    return { months, data, monthTotals };
  }, [rows]);

  const pivotColumns = [
    { title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 120, fixed: 'left' },
    { title: 'Job',  dataIndex: 'JobName',  key: 'JobName',  width: 90,  fixed: 'left', render: (v) => v || '—' },
    ...pivot.months.map((m) => ({
      title: dayjs(`${m}-01`).format('MMM YYYY'),
      key: `m_${m}`,
      dataIndex: `m_${m}`,
      width: 110,
      align: 'center',
      render: (val) => {
        if (!val) return <span style={{ color: '#bbb' }}>—</span>;
        return (
          <div style={{ lineHeight: 1.5 }}>
            <div style={{ fontSize: 11, color: '#888' }}>Order Count FTE: {val.orderCount.toFixed(1)}</div>
            <div style={{ fontSize: 11, color: '#888' }}>Accepted Offer: {val.acceptedOffer}</div>
            <div style={{ fontSize: 11, color: '#888' }}>No Show: {val.noShow}</div>
            <div style={{ fontSize: 11, color: '#888' }}>Started Orientation: {val.orientationFallout + val.inTraining + val.formerTrainee + val.grads}</div>
            <div style={{ fontSize: 11, color: '#888' }}>Orientation Fallout: {val.orientationFallout}</div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>In Training: {val.inTraining}</div>
            <div style={{ fontSize: 11, color: '#888' }}>Grad Target FTE: {val.gradTargetFTE.toFixed(1)}</div>
            <div style={{ fontSize: 11, color: '#888' }}>Grads FTE: {val.gradFTE.toFixed(1)}</div>
            <div style={{ fontWeight: 700, fontSize: 13 }}><span style={{ fontWeight: 400, color: '#888', fontSize: 11 }}>Grads: </span>{val.grads}</div>
            <div style={{ fontSize: 11, color: '#888' }}>Grad% Trainer: {fmtFillPct(val.grads, val.formerTrainee + val.grads)}</div>
            <div style={{ fontSize: 13, fontWeight: 700 }}><span style={{ fontWeight: 400, color: '#888', fontSize: 11 }}>Grad%: </span>{fmtFillPct(val.grads, val.orientationFallout + val.formerTrainee + val.grads)}</div>
            <div style={{ fontSize: 13, fontWeight: 700 }}><span style={{ fontWeight: 400, color: '#888', fontSize: 11 }}>Still Employed: </span>{val.stillEmployed}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#c0392b', marginTop: 4 }}>Termed:</div>
            <div style={{ fontSize: 11, color: '#c0392b' }}>Wk1 (0-6d): {val.t0}</div>
            <div style={{ fontSize: 11, color: '#c0392b' }}>Wk2 (7-13d): {val.t7}</div>
            <div style={{ fontSize: 11, color: '#c0392b' }}>Wk3 (14-20d): {val.t14}</div>
            <div style={{ fontSize: 11, color: '#e67e22' }}>21-60d: {val.t21}</div>
            <div style={{ fontSize: 11, color: '#e67e22' }}>61-180d: {val.t61}</div>
            <div style={{ fontSize: 11, color: '#888' }}>181+d: {val.t181}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#555', borderTop: '1px solid #eee', marginTop: 2, paddingTop: 2 }}>Total Terms: {val.t0 + val.t7 + val.t14 + val.t21 + val.t61 + val.t181}</div>
          </div>
        );
      },
    })),
    {
      title: 'Total', key: '_total', dataIndex: '_total', width: 130, align: 'center',
      fixed: 'right',
      render: (val) => {
        if (!val || (val.grads === 0 && val.formerTrainee === 0)) return <span style={{ color: '#bbb' }}>—</span>;
        return (
          <div style={{ lineHeight: 1.5 }}>
            <div style={{ fontSize: 11, color: '#888' }}>Order Count FTE: {val.orderCount.toFixed(1)}</div>
            <div style={{ fontSize: 11, color: '#888' }}>Accepted Offer: {val.acceptedOffer}</div>
            <div style={{ fontSize: 11, color: '#888' }}>No Show: {val.noShow}</div>
            <div style={{ fontSize: 11, color: '#888' }}>Started Orientation: {val.orientationFallout + val.inTraining + val.formerTrainee + val.grads}</div>
            <div style={{ fontSize: 11, color: '#888' }}>Orientation Fallout: {val.orientationFallout}</div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>In Training: {val.inTraining}</div>
            <div style={{ fontSize: 11, color: '#888' }}>Grad Target FTE: {val.gradTargetFTE.toFixed(1)}</div>
            <div style={{ fontSize: 11, color: '#888' }}>Grads FTE: {val.gradFTE.toFixed(1)}</div>
            <div style={{ fontWeight: 700, fontSize: 13 }}><span style={{ fontWeight: 400, color: '#888', fontSize: 11 }}>Grads: </span>{val.grads}</div>
            <div style={{ fontSize: 11, color: '#888' }}>Grad% Trainer: {fmtFillPct(val.grads, val.formerTrainee + val.grads)}</div>
            <div style={{ fontSize: 13, fontWeight: 700 }}><span style={{ fontWeight: 400, color: '#888', fontSize: 11 }}>Grad%: </span>{fmtFillPct(val.grads, val.orientationFallout + val.formerTrainee + val.grads)}</div>
            <div style={{ fontSize: 13, fontWeight: 700 }}><span style={{ fontWeight: 400, color: '#888', fontSize: 11 }}>Still Employed: </span>{val.stillEmployed}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#c0392b', marginTop: 4 }}>Termed:</div>
            <div style={{ fontSize: 11, color: '#c0392b' }}>Wk1 (0-6d): {val.t0}</div>
            <div style={{ fontSize: 11, color: '#c0392b' }}>Wk2 (7-13d): {val.t7}</div>
            <div style={{ fontSize: 11, color: '#c0392b' }}>Wk3 (14-20d): {val.t14}</div>
            <div style={{ fontSize: 11, color: '#e67e22' }}>21-60d: {val.t21}</div>
            <div style={{ fontSize: 11, color: '#e67e22' }}>61-180d: {val.t61}</div>
            <div style={{ fontSize: 11, color: '#888' }}>181+d: {val.t181}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#555', borderTop: '1px solid #eee', marginTop: 2, paddingTop: 2 }}>Total Terms: {val.t0 + val.t7 + val.t14 + val.t21 + val.t61 + val.t181}</div>
          </div>
        );
      },
    },
  ];

  // One row per team, for the Summary views
  const teamRows = useMemo(() => groupRowsByTeam(rows, SUM_FIELDS), [rows]);

  // ── Detail table totals ──────────────────────────────────────────────────────
  const totals = rows.reduce((acc, r) => {
    acc.OrderCount          += Number(r.OrderCount          ?? 0);
    acc.GraduationTargetFTE += Number(r.GraduationTargetFTE ?? 0);
    acc.AcceptedOffer       += r.AcceptedOffer;
    acc.RescindedOffer      += r.RescindedOffer;
    acc.NoShow              += r.NoShow;
    acc.OrientationFallout  += r.OrientationFallout;
    acc.InTraining          += r.InTraining;
    acc.FormerTrainee       += r.FormerTrainee;
    acc.InProduction        += r.InProduction;
    acc.FormerEmployee      += r.FormerEmployee;
    acc.InterTransfer       += r.InterTransfer;
    acc.VoluntaryTerms      += r.VoluntaryTerms   ?? 0;
    acc.InvoluntaryTerms    += r.InvoluntaryTerms ?? 0;
    acc.InvoluntaryTraineeTerms += r.InvoluntaryTraineeTerms ?? 0;
    acc.StillEmployed       += r.StillEmployed   ?? 0;
    acc.Grads60Eligible     += r.Grads60Eligible ?? 0;
    acc.Grads60Retained     += r.Grads60Retained ?? 0;
    acc.Terms0to6           += r.Terms0to6    ?? 0;
    acc.Terms7to13          += r.Terms7to13   ?? 0;
    acc.Terms14to20         += r.Terms14to20  ?? 0;
    acc.Terms21to60         += r.Terms21to60  ?? 0;
    acc.Terms61to180        += r.Terms61to180 ?? 0;
    acc.Terms181plus        += r.Terms181plus ?? 0;
    acc.GradFTE             += r.GradFTE ?? 0;
    acc.InFlightFTE         += r.InFlightFTE ?? 0;
    acc.OrderFillFTENumerator += r.OrderFillFTENumerator ?? 0;
    return acc;
  }, { OrderCount: 0, GraduationTargetFTE: 0, AcceptedOffer: 0, RescindedOffer: 0, NoShow: 0, OrientationFallout: 0, InTraining: 0, FormerTrainee: 0, InProduction: 0, FormerEmployee: 0, InterTransfer: 0, VoluntaryTerms: 0, InvoluntaryTerms: 0, InvoluntaryTraineeTerms: 0, StillEmployed: 0, Grads60Eligible: 0, Grads60Retained: 0, Terms0to6: 0, Terms7to13: 0, Terms14to20: 0, Terms21to60: 0, Terms61to180: 0, Terms181plus: 0, GradFTE: 0, InFlightFTE: 0, OrderFillFTENumerator: 0 });

  const totalGrads   = totals.InProduction + totals.FormerEmployee + totals.InterTransfer;
  const totalStartedOrientation = totals.OrientationFallout + totals.FormerTrainee + totals.InProduction + totals.FormerEmployee + totals.InterTransfer;
  const totalGradPct = fmtFillPct(totalGrads, totalStartedOrientation);
  // Grad % with the involuntary terms (people we let go) taken out: how many
  // of the ones we wanted to keep made it through.
  const totalGradPctVoluntary = fmtFillPct(totalGrads, totalStartedOrientation - totals.InvoluntaryTerms);
  // Grad % Trainer with the Former Trainees we let go (involuntary) taken out:
  // how many of the trainees we wanted to keep made it through.
  const totalGradPctTrainerVoluntary = fmtFillPct(
    totalGrads,
    totals.FormerTrainee + totals.InProduction + totals.FormerEmployee + totals.InterTransfer - totals.InvoluntaryTraineeTerms,
  );

  const todayStr = dayjs().format('YYYY-MM-DD');
  const startedTotals = rows
    .filter((r) => r.StartDate <= todayStr)
    .reduce(
      (acc, r) => ({
        grads: acc.grads + r.InProduction + r.FormerEmployee + r.InterTransfer,
        gradFTE: acc.gradFTE + (r.GradFTE ?? 0),
        target: acc.target + Number(r.GraduationTargetFTE ?? 0),
        inFlight: acc.inFlight + Number(r.InFlightFTE ?? 0),
      }),
      { grads: 0, gradFTE: 0, target: 0, inFlight: 0 }
    );
  const startedAboveTarget = sumGradsAboveTarget(rows.filter((r) => r.StartDate <= todayStr));

  const detailColumns = [
    { title: 'Start Date',          dataIndex: 'StartDate',          key: 'StartDate',          width: 110, fixed: 'left', sorter: (a, b) => (a.StartDate ?? '').localeCompare(b.StartDate ?? '') },
    { title: 'Team',                dataIndex: 'TeamName',           key: 'TeamName',           width: 120, fixed: 'left', sorter: (a, b) => (a.TeamName ?? '').localeCompare(b.TeamName ?? '') },
    { title: 'Job',                 dataIndex: 'JobName',            key: 'JobName',            width: 90, fixed: 'left', render: (v) => v || '—', sorter: (a, b) => (a.JobName ?? '').localeCompare(b.JobName ?? '') },
    { title: 'Order Count FTE',      dataIndex: 'OrderCount',         key: 'OrderCount',         width: 130, align: 'right', sorter: (a, b) => (a.OrderCount ?? 0) - (b.OrderCount ?? 0),
      render: (v) => v != null ? Number(v).toFixed(1) : '—' },
    {
      title: 'Order Fill % FTE', key: 'OrderFillFTEPct', width: 100, align: 'right',
      sorter: (a, b) => {
        const pctA = a.OrderCount > 0 ? (a.OrderFillFTENumerator ?? 0) / a.OrderCount : 0;
        const pctB = b.OrderCount > 0 ? (b.OrderFillFTENumerator ?? 0) / b.OrderCount : 0;
        return pctA - pctB;
      },
      render: (_, r) => fmtFillPct(r.OrderFillFTENumerator ?? 0, r.OrderCount),
    },
    { title: 'Grad Target FTE',     dataIndex: 'GraduationTargetFTE', key: 'GradTarget',        width: 130, align: 'right', sorter: (a, b) => (a.GraduationTargetFTE ?? 0) - (b.GraduationTargetFTE ?? 0),
      render: (v) => v != null ? Number(v).toFixed(1) : '—' },
    {
      title: 'Grads FTE', key: 'GradFTE', width: 95, align: 'right',
      sorter: (a, b) => (a.GradFTE ?? 0) - (b.GradFTE ?? 0),
      render: (_, r) => (r.GradFTE ?? 0).toFixed(1),
    },
    {
      title: 'Grads', key: 'Grads', width: 90, align: 'right',
      sorter: (a, b) => (a.InProduction + a.FormerEmployee + a.InterTransfer) - (b.InProduction + b.FormerEmployee + b.InterTransfer),
      render: (_, r) => r.InProduction + r.FormerEmployee + r.InterTransfer,
    },
    { title: 'Still Employed',      dataIndex: 'StillEmployed',      key: 'StillEmployed',      width: 110, align: 'right', sorter: (a, b) => (a.StillEmployed ?? 0) - (b.StillEmployed ?? 0) },
    {
      title: 'Grad %', key: 'GradPct', width: 90, align: 'right',
      sorter: (a, b) => {
        const gradsA = a.InProduction + a.FormerEmployee + a.InterTransfer;
        const denomA = a.OrientationFallout + a.FormerTrainee + a.InProduction + a.FormerEmployee + a.InterTransfer;
        const gradsB = b.InProduction + b.FormerEmployee + b.InterTransfer;
        const denomB = b.OrientationFallout + b.FormerTrainee + b.InProduction + b.FormerEmployee + b.InterTransfer;
        return (denomA > 0 ? gradsA / denomA : 0) - (denomB > 0 ? gradsB / denomB : 0);
      },
      render: (_, r) => {
        const grads = r.InProduction + r.FormerEmployee + r.InterTransfer;
        const denom = r.OrientationFallout + r.FormerTrainee + r.InProduction + r.FormerEmployee + r.InterTransfer;
        return fmtFillPct(grads, denom);
      },
    },
    {
      title: 'Grad % Trainer', key: 'GradPctTrainer', width: 120, align: 'right',
      sorter: (a, b) => {
        const gradsA = a.InProduction + a.FormerEmployee + a.InterTransfer;
        const denomA = a.FormerTrainee + a.InProduction + a.FormerEmployee + a.InterTransfer;
        const gradsB = b.InProduction + b.FormerEmployee + b.InterTransfer;
        const denomB = b.FormerTrainee + b.InProduction + b.FormerEmployee + b.InterTransfer;
        return (denomA > 0 ? gradsA / denomA : 0) - (denomB > 0 ? gradsB / denomB : 0);
      },
      render: (_, r) => {
        const grads = r.InProduction + r.FormerEmployee + r.InterTransfer;
        const denom = r.FormerTrainee + r.InProduction + r.FormerEmployee + r.InterTransfer;
        return fmtFillPct(grads, denom);
      },
    },
    {
      title: (
        <Tooltip title="Grad FTE against target, with seats still In Training or on an Accepted Offer held out of the target. An unfinished class is not counted as behind for seats that have yet to be decided.">
          FTE Grads Above Target <InfoCircleOutlined style={{ fontSize: 11, color: '#888' }} />
        </Tooltip>
      ),
      key: 'GradsAboveTarget', width: 165, align: 'right',
      sorter: (a, b) => (gradsAboveTarget(a) ?? 0) - (gradsAboveTarget(b) ?? 0),
      render: (_, r) => {
        const val = gradsAboveTarget(r);
        if (val == null) return '—';
        if (r.StartDate > dayjs().format('YYYY-MM-DD')) return <Typography.Text type="secondary">—</Typography.Text>;
        const color = val > 0 ? '#52c41a' : val < 0 ? '#ff4d4f' : undefined;
        const inFlight = Number(r.InFlightFTE ?? 0);
        const cell = (
          <span style={{ color }}>{val > 0 ? `+${val.toFixed(1)}` : val.toFixed(1)}</span>
        );
        return inFlight > 0
          ? (
            <Tooltip title={`${inFlight.toFixed(1)} FTE still in flight, held out of the ${Number(r.GraduationTargetFTE).toFixed(1)} target`}>
              <span style={{ borderBottom: '1px dashed #d46b08', cursor: 'help' }}>{cell}</span>
            </Tooltip>
          )
          : cell;
      },
    },
    {
      title: (
        <Tooltip title={`% of Grads employed for more than ${RETENTION_DAYS} days after graduation. Only grads whose graduation is already ${RETENTION_DAYS}+ days in the past are counted, since a recent class has not had the chance to reach it yet`}>
          % Grads Empl {RETENTION_DAYS}d+ <InfoCircleOutlined style={{ fontSize: 11, color: '#888' }} />
        </Tooltip>
      ),
      key: 'Grads60Pct', width: 130, align: 'right',
      sorter: (a, b) => retentionPct(a) - retentionPct(b),
      render: (_, r) => <RetentionCell row={r} />,
    },
    { title: 'Accepted Offer',      dataIndex: 'AcceptedOffer',      key: 'AcceptedOffer',      width: 120, align: 'right', sorter: (a, b) => a.AcceptedOffer - b.AcceptedOffer },
    { title: 'Rescinded Offer',     dataIndex: 'RescindedOffer',     key: 'RescindedOffer',     width: 130, align: 'right', sorter: (a, b) => a.RescindedOffer - b.RescindedOffer },
    { title: 'No Show',             dataIndex: 'NoShow',             key: 'NoShow',             width: 90,  align: 'right', sorter: (a, b) => a.NoShow - b.NoShow },
    { title: 'Orientation Fallout', dataIndex: 'OrientationFallout', key: 'OrientationFallout', width: 150, align: 'right', sorter: (a, b) => a.OrientationFallout - b.OrientationFallout },
    { title: 'In Training',         dataIndex: 'InTraining',         key: 'InTraining',         width: 105, align: 'right', sorter: (a, b) => a.InTraining - b.InTraining },
    { title: 'Former Trainee',      dataIndex: 'FormerTrainee',      key: 'FormerTrainee',      width: 120, align: 'right', sorter: (a, b) => a.FormerTrainee - b.FormerTrainee },
    { title: 'In Production',       dataIndex: 'InProduction',       key: 'InProduction',       width: 115, align: 'right', sorter: (a, b) => a.InProduction - b.InProduction },
    { title: 'Former Employee',     dataIndex: 'FormerEmployee',     key: 'FormerEmployee',     width: 130, align: 'right', sorter: (a, b) => a.FormerEmployee - b.FormerEmployee },
    { title: 'Inter Transfer',      dataIndex: 'InterTransfer',      key: 'InterTransfer',      width: 115, align: 'right', sorter: (a, b) => a.InterTransfer - b.InterTransfer },
    { title: 'Wk1 (0-6d)',    dataIndex: 'Terms0to6',   key: 'Terms0to6',   width: 105, align: 'right', sorter: (a, b) => (a.Terms0to6   ?? 0) - (b.Terms0to6   ?? 0) },
    { title: 'Wk2 (7-13d)',  dataIndex: 'Terms7to13',  key: 'Terms7to13',  width: 110, align: 'right', sorter: (a, b) => (a.Terms7to13  ?? 0) - (b.Terms7to13  ?? 0) },
    { title: 'Wk3 (14-20d)', dataIndex: 'Terms14to20', key: 'Terms14to20', width: 115, align: 'right', sorter: (a, b) => (a.Terms14to20 ?? 0) - (b.Terms14to20 ?? 0) },
    { title: 'Terms 21-60d', dataIndex: 'Terms21to60', key: 'Terms21to60', width: 115, align: 'right', sorter: (a, b) => (a.Terms21to60 ?? 0) - (b.Terms21to60 ?? 0) },
    { title: 'Terms 61-180d',dataIndex: 'Terms61to180',key: 'Terms61to180',width: 115, align: 'right', sorter: (a, b) => (a.Terms61to180 ?? 0) - (b.Terms61to180 ?? 0) },
    { title: 'Terms 181+d',  dataIndex: 'Terms181plus',key: 'Terms181plus',width: 110, align: 'right', sorter: (a, b) => (a.Terms181plus ?? 0) - (b.Terms181plus ?? 0) },
    {
      title: 'Total Terms', key: 'TotalTerms', width: 115, align: 'right',
      sorter: (a, b) => ((a.Terms0to6 ?? 0) + (a.Terms7to13 ?? 0) + (a.Terms14to20 ?? 0) + (a.Terms21to60 ?? 0) + (a.Terms61to180 ?? 0) + (a.Terms181plus ?? 0))
                      - ((b.Terms0to6 ?? 0) + (b.Terms7to13 ?? 0) + (b.Terms14to20 ?? 0) + (b.Terms21to60 ?? 0) + (b.Terms61to180 ?? 0) + (b.Terms181plus ?? 0)),
      render: (_, r) => (r.Terms0to6 ?? 0) + (r.Terms7to13 ?? 0) + (r.Terms14to20 ?? 0) + (r.Terms21to60 ?? 0) + (r.Terms61to180 ?? 0) + (r.Terms181plus ?? 0),
    },
  ];

  const returnTotals = useMemo(() => returnRows.reduce((acc, r) => ({
    TotalInClass:       acc.TotalInClass       + (r.TotalInClass       ?? 0),
    StartedOrientation: acc.StartedOrientation + (r.StartedOrientation ?? 0),
    GradCount:          acc.GradCount          + (r.GradCount          ?? 0),
    TrainingHours:      acc.TrainingHours      + (r.TrainingHours      ?? 0),
    BillableHours:      acc.BillableHours      + (r.BillableHours      ?? 0),
    TrainingCost:       acc.TrainingCost       + (r.TrainingCost       ?? 0),
    BillableRevenue:    acc.BillableRevenue    + (r.BillableRevenue    ?? 0),
    NetReturn:          acc.NetReturn          + (r.NetReturn          ?? 0),
  }), { TotalInClass: 0, StartedOrientation: 0, GradCount: 0, TrainingHours: 0, BillableHours: 0, TrainingCost: 0, BillableRevenue: 0, NetReturn: 0 }), [returnRows]);

  const returnTeamRows = useMemo(
    () => groupRowsByTeam(returnRows, RETURN_SUM_FIELDS, RETURN_RATE_FIELDS), [returnRows]);

  const costTotals = useMemo(() => costRows.reduce((acc, r) => ({
    NonGradCount:        acc.NonGradCount        + (r.NonGradCount        ?? 0),
    TotalHours:          acc.TotalHours          + (r.TotalHours          ?? 0),
    NonGradsWages:       acc.NonGradsWages       + (r.NonGradsWages       ?? 0),
    NonGradsHiringCosts: acc.NonGradsHiringCosts + (r.NonGradsHiringCosts ?? 0),
    NonGradsTotalCost:   acc.NonGradsTotalCost   + (r.NonGradsTotalCost   ?? 0),
  }), { NonGradCount: 0, TotalHours: 0, NonGradsWages: 0, NonGradsHiringCosts: 0, NonGradsTotalCost: 0 }), [costRows]);

  const costTeamRows = useMemo(
    () => groupRowsByTeam(costRows, COST_SUM_FIELDS, COST_RATE_FIELDS), [costRows]);

  const costColumns = [
    { title: 'Start Date', dataIndex: 'StartDate',    key: 'StartDate',    width: 110, sorter: (a, b) => (a.StartDate ?? '').localeCompare(b.StartDate ?? '') },
    { title: 'Team',       dataIndex: 'TeamName',     key: 'TeamName',     width: 120, sorter: (a, b) => (a.TeamName ?? '').localeCompare(b.TeamName ?? '') },
    { title: 'Job',        dataIndex: 'JobName',      key: 'JobName',      width: 90,  render: (v) => v || '—', sorter: (a, b) => (a.JobName ?? '').localeCompare(b.JobName ?? '') },
    { title: 'Non-Grad Count', dataIndex: 'NonGradCount',        key: 'NonGradCount',        width: 120, align: 'right', sorter: (a, b) => (a.NonGradCount        ?? 0) - (b.NonGradCount        ?? 0) },
    { title: 'Non-Grad Total Hrs', dataIndex: 'TotalHours', key: 'TotalHours', width: 140, align: 'right', sorter: (a, b) => (a.TotalHours ?? 0) - (b.TotalHours ?? 0), render: (v) => v != null ? Number(v).toFixed(1) : '—' },
    {
      title: 'Non-Grads Wages', dataIndex: 'NonGradsWages', key: 'NonGradsWages', width: 130, align: 'right',
      sorter: (a, b) => (a.NonGradsWages ?? 0) - (b.NonGradsWages ?? 0),
      render: (v, r) => r.TrainingWage == null ? <span style={{ color: '#aaa' }}>—</span> : fmtCurrency(v),
    },
    {
      title: 'Non-Grads Hiring Costs', dataIndex: 'NonGradsHiringCosts', key: 'NonGradsHiringCosts', width: 160, align: 'right',
      sorter: (a, b) => (a.NonGradsHiringCosts ?? 0) - (b.NonGradsHiringCosts ?? 0),
      render: (v, r) => r.HireCost == null ? <span style={{ color: '#aaa' }}>—</span> : fmtCurrency(v),
    },
    {
      title: 'Non-Grads Total Cost', dataIndex: 'NonGradsTotalCost', key: 'NonGradsTotalCost', width: 150, align: 'right',
      sorter: (a, b) => (a.NonGradsTotalCost ?? 0) - (b.NonGradsTotalCost ?? 0),
      render: (v, r) => (r.TrainingWage == null && r.HireCost == null) ? <span style={{ color: '#aaa' }}>—</span> : <strong>{fmtCurrency(v)}</strong>,
    },
  ];

  const returnColumns = [
    { title: 'Start Date', dataIndex: 'StartDate',    key: 'StartDate',    width: 110, sorter: (a, b) => (a.StartDate ?? '').localeCompare(b.StartDate ?? '') },
    { title: 'Team',       dataIndex: 'TeamName',     key: 'TeamName',     width: 120, sorter: (a, b) => (a.TeamName ?? '').localeCompare(b.TeamName ?? '') },
    { title: 'Job',        dataIndex: 'JobName',      key: 'JobName',      width: 90,  render: (v) => v || '—', sorter: (a, b) => (a.JobName ?? '').localeCompare(b.JobName ?? '') },
    { title: 'Accepted Offer',     dataIndex: 'TotalInClass',      key: 'TotalInClass',      width: 120, align: 'right', sorter: (a, b) => (a.TotalInClass ?? 0) - (b.TotalInClass ?? 0) },
    { title: 'Started Orientation', dataIndex: 'StartedOrientation', key: 'StartedOrientation', width: 150, align: 'right', sorter: (a, b) => (a.StartedOrientation ?? 0) - (b.StartedOrientation ?? 0) },
    { title: 'Grads',              dataIndex: 'GradCount',          key: 'GradCount',          width: 80,  align: 'right', sorter: (a, b) => (a.GradCount ?? 0) - (b.GradCount ?? 0) },
    {
      title: 'Grads Billable After', dataIndex: 'BillableAfterDays', key: 'BillableAfterDays', width: 140, align: 'right',
      sorter: (a, b) => (a.BillableAfterDays ?? 0) - (b.BillableAfterDays ?? 0),
      render: (v) => v != null ? `Day ${v}` : <span style={{ color: '#aaa' }}>—</span>,
    },
    {
      title: (
        <Tooltip title="Timecard Hrs of first 78 days of employment">
          Timecard Hrs 0-78d <InfoCircleOutlined style={{ fontSize: 11, color: '#888' }} />
        </Tooltip>
      ),
      dataIndex: 'TrainingHours', key: 'TrainingHours', width: 140, align: 'right',
      sorter: (a, b) => (a.TrainingHours ?? 0) - (b.TrainingHours ?? 0),
      render: (v) => v != null ? Number(v).toFixed(1) : '—',
    },
    {
      title: (
        <Tooltip title="Billable Hrs from Graduation Date to day 78 of employment">
          Billable Hrs Grad-78d <InfoCircleOutlined style={{ fontSize: 11, color: '#888' }} />
        </Tooltip>
      ),
      dataIndex: 'BillableHours', key: 'BillableHours', width: 145, align: 'right',
      sorter: (a, b) => (a.BillableHours ?? 0) - (b.BillableHours ?? 0),
      render: (v, r) => r.BillableRate == null ? <span style={{ color: '#aaa' }}>—</span> : Number(v).toFixed(1),
    },
    {
      title: (
        <Tooltip title="Training Hrs x Training Wage — covers all class members for the full 78-day window">
          Training & Wages Cost <InfoCircleOutlined style={{ fontSize: 11, color: '#888' }} />
        </Tooltip>
      ),
      dataIndex: 'TrainingCost', key: 'TrainingCost', width: 135, align: 'right',
      sorter: (a, b) => (a.TrainingCost ?? 0) - (b.TrainingCost ?? 0),
      render: (v, r) => r.TrainingWage == null ? <span style={{ color: '#aaa' }}>—</span> : fmtCurrency(v),
    },
    // Below Management the column has no figure to show for any row, so it is
    // left out rather than drawn as a column of dashes. Net Return stays,
    // because at Basic it still says Positive or Negative.
    //
    // The summary row below pushes cells positionally, so it skips this one
    // at the same level -- otherwise every total after it lands one column to
    // the left.
    ...(canViewFinancials >= 2 ? [{
      title: (
        <Tooltip title="Billable Hrs x Billable Rate — grads only, from the 'Billable After' day through day 78">
          Billable Revenue <InfoCircleOutlined style={{ fontSize: 11, color: '#888' }} />
        </Tooltip>
      ),
      dataIndex: 'BillableRevenue', key: 'BillableRevenue', width: 150, align: 'right',
      sorter: (a, b) => (a.BillableRevenue ?? 0) - (b.BillableRevenue ?? 0),
      render: (v, r) => {
        // A dash here means no billable rate is configured, which is a real
        // answer rather than a hidden one.
        if (r.BillableRate == null) return <span style={{ color: '#aaa' }}>—</span>;
        return fmtCurrency(v);
      },
    }] : []),
    {
      title: 'Net Return', dataIndex: 'NetReturn', key: 'NetReturn', width: 120, align: 'right',
      sorter: (a, b) => (a.NetReturn ?? 0) - (b.NetReturn ?? 0),
      render: (v, r) => {
        if (r.TrainingWage == null && r.BillableRate == null) return <span style={{ color: '#aaa' }}>—</span>;
        if (canViewFinancials === 1) {
          const color = v > 0 ? '#52c41a' : '#ff4d4f';
          return <strong style={{ color }}>{v > 0 ? 'Positive' : 'Negative'}</strong>;
        }
        // Guard explicitly rather than relying on the outer >= 1 gate around
        // this table, so the column cannot leak a figure if reused elsewhere.
        if (canViewFinancials < 2) return <span style={{ color: '#aaa' }}>—</span>;
        const color = v > 0 ? '#52c41a' : v < 0 ? '#ff4d4f' : undefined;
        return <strong style={{ color }}>{v > 0 ? `+${fmtCurrency(v)}` : fmtCurrency(v)}</strong>;
      },
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space align="center">
          <Typography.Title level={4} style={{ margin: 0 }}>Recruitment Stats</Typography.Title>
          <Popover
            title="Metric Definitions"
            trigger="hover"
            content={
              <div style={{ maxWidth: 340 }}>
                <p style={{ margin: '0 0 6px' }}><strong>Order Fill % FTE</strong> — FTE of those who attended at least Orientation day (ScheduledHours / 40) divided by Order Count FTE</p>
                <p style={{ margin: '0 0 6px' }}><strong>Grad %</strong> — Grads / Concluded (headcount); Concluded = Orientation Fallout + Former Trainee + In Production + Former Employee + Inter Transfer (excludes In Training — outcome not yet known)</p>
                <p style={{ margin: '0 0 6px' }}><strong>Grad % Voluntary</strong> — Grad % with Involuntary Terms (terminated / laid off in Paycor) removed from the denominator</p>
                <p style={{ margin: '0 0 6px' }}><strong>Grad % Trainer Voluntary</strong> — Grad % Trainer with involuntary Former Trainees (terminated / laid off in Paycor) removed from the denominator</p>
                <p style={{ margin: '0 0 6px' }}><strong>Voluntary / Involuntary Term</strong> — of Orientation Fallout and Former Trainee: resigned or retired / terminated or laid off in Paycor</p>
                <p style={{ margin: '0 0 6px' }}><strong>Grad % Trainer</strong> — Grads / (Former Trainee + In Production + Former Employee + Inter Transfer); excludes Orientation Fallout and In Training from denominator</p>
                <p style={{ margin: '0 0 6px' }}><strong>Grads FTE</strong> — Sum of (ScheduledHours / 40) for grads (In Production, Former Employee, Inter Transfer)</p>
                <p style={{ margin: 0 }}><strong>FTE Grads Above Target</strong> — Grad FTE minus Grad Target FTE</p>
              </div>
            }
          >
            <QuestionCircleOutlined style={{ fontSize: 15, color: '#8c8c8c', cursor: 'default' }} />
          </Popover>
        </Space>
      </div>

      <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
        <Col xs={24} md={10}>
          <RangePicker
            style={{ width: '100%' }}
            format="YYYY-MM-DD"
            placeholder={['Start date from', 'Start date to']}
            allowEmpty={[true, true]}
            onChange={(dates) => setDateRange(dates ?? [null, null])}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Select
            showSearch optionFilterProp="label" allowClear style={{ width: '100%' }}
            placeholder="Filter by team"
            value={teamId}
            options={teams.map((t) => ({ value: t.ID, label: t.Name }))}
            onChange={(v) => setTeamId(v ?? null)}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Select
            showSearch optionFilterProp="label" allowClear style={{ width: '100%' }}
            placeholder="Filter by job"
            value={jobId}
            options={jobs.map((j) => ({ value: j.JobID, label: j.JobName }))}
            onChange={(v) => setJobId(v ?? null)}
          />
        </Col>
        <Col xs={24} sm={12} md={2}>
          <Button icon={<SearchOutlined />} onClick={load} style={{ width: '100%' }}>Search</Button>
        </Col>
      </Row>

      {/* ── Scorecards ── */}
      {rows.length > 0 && (
        <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
          {/* Orders carries two stats per row, Grad Stats now four, so it
              yields a unit to keep the wider card from cramping */}
          <Col xs={24} sm={12} lg={5}>
            <Card size="small" title="Orders" styles={{ body: { padding: '8px 12px' } }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="Order Count FTE"
                    value={totals.OrderCount.toFixed(1)}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Order Fill % FTE"
                    value={fmtFillPct(totals.OrderFillFTENumerator, totals.OrderCount)}
                  />
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col span={12}>
                  <Statistic title="Accepted Offer" value={totals.AcceptedOffer} />
                </Col>
                <Col span={12}>
                  <Statistic title="In Training" value={totals.InTraining} />
                </Col>
              </Row>
              {/* Everyone who got that far, the ones still in training
                  included -- as the pivot's Started Orientation. Not the Grad %
                  denominators, which leave In Training out until they finish. */}
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col span={12}>
                  <Statistic title="Started Orientation" value={totals.OrientationFallout + totals.InTraining + totals.FormerTrainee + totalGrads} />
                </Col>
                <Col span={12}>
                  <Statistic title="Started Training" value={totals.InTraining + totals.FormerTrainee + totalGrads} />
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={11}>
            <Card size="small" title="Grad Stats" styles={{ body: { padding: '8px 12px' } }}>
              {/* Four across rather than three, to fit the retention metric */}
              <Row gutter={[12, 8]}>
                <Col span={6}>
                  <Statistic title="Grad Target FTE" value={totals.GraduationTargetFTE.toFixed(1)} />
                </Col>
                <Col span={6}>
                  <Statistic title="Grads FTE" value={totals.GradFTE.toFixed(1)} />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="FTE Grads Above Target"
                    value={(() => {
                      if (!startedTotals.target) return '—';
                      const val = startedAboveTarget;
                      return val > 0 ? `+${val.toFixed(1)}` : val.toFixed(1);
                    })()}
                    valueStyle={(() => {
                      if (!startedTotals.target) return {};
                      const val = startedAboveTarget;
                      return { color: val > 0 ? '#52c41a' : val < 0 ? '#ff4d4f' : undefined };
                    })()}
                  />
                </Col>
                <Col span={6}>
                  <Statistic title="Still Employed" value={totals.StillEmployed} />
                </Col>
              </Row>
              <Row gutter={[12, 8]} style={{ marginTop: 8 }}>
                <Col span={6}>
                  <Statistic title="Grads" value={totalGrads} />
                </Col>
                <Col span={6}>
                  <Statistic title="Grad %" value={totalGradPct} />
                </Col>
                <Col span={6}>
                  <Statistic title="Grad % Trainer" value={fmtFillPct(totalGrads, totals.FormerTrainee + totals.InProduction + totals.FormerEmployee + totals.InterTransfer)} />
                </Col>
                <Col span={6}>
                  <Tooltip title={`% of Grads employed for more than ${RETENTION_DAYS} days after graduation. Only grads whose graduation is already ${RETENTION_DAYS}+ days in the past are counted, since a recent class has not had the chance to reach it yet`}>
                    <Statistic
                      title={<span>% Grads Empl {RETENTION_DAYS}d+ <InfoCircleOutlined style={{ fontSize: 11, color: '#888' }} /></span>}
                      value={retentionPct(totals) == null ? '—' : `${retentionPct(totals).toFixed(1)}%`}
                      valueStyle={retentionPct(totals) == null
                        ? { color: '#bbb' }
                        : { color: retentionPct(totals) >= RETENTION_GOAL_PCT ? '#52c41a' : '#ff4d4f' }}
                    />
                  </Tooltip>
                </Col>
              </Row>
              {/* Of the ones who did not graduate: did they leave, or were they let go */}
              <Row gutter={[12, 8]} style={{ marginTop: 8 }}>
                <Col span={6}>
                  <Statistic title="Voluntary Term" value={totals.VoluntaryTerms} />
                </Col>
                <Col span={6}>
                  <Statistic title="Involuntary Term" value={totals.InvoluntaryTerms} />
                </Col>
                <Col span={6}>
                  <Statistic title="Grad % Voluntary" value={totalGradPctVoluntary} />
                </Col>
                <Col span={6}>
                  <Statistic title="Grad % Trainer Voluntary" value={totalGradPctTrainerVoluntary} />
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card size="small" title="Term Bucket" styles={{ body: { padding: '8px 12px' } }}>
              <Row gutter={[16, 8]}>
                {[
                  { bucket: '0-6',    label: 'Wk1 (0-6d)',   value: totals.Terms0to6,    color: '#c0392b' },
                  { bucket: '7-13',   label: 'Wk2 (7-13d)',  value: totals.Terms7to13,   color: '#c0392b' },
                  { bucket: '14-20',  label: 'Wk3 (14-20d)', value: totals.Terms14to20,  color: '#c0392b' },
                  { bucket: '21-60',  label: '21-60d',        value: totals.Terms21to60,  color: '#e67e22' },
                  { bucket: '61-180', label: '61-180d',       value: totals.Terms61to180, color: '#e67e22' },
                  { bucket: '181+',   label: '181+d',         value: totals.Terms181plus, color: undefined },
                ].map(({ bucket, label, value, color }) => (
                  <Col key={bucket} span={8}>
                    <div
                      onClick={() => value > 0 && openTermDrawer(bucket, label)}
                      style={{ cursor: value > 0 ? 'pointer' : 'default', borderRadius: 6, padding: '2px 4px', transition: 'background 0.15s' }}
                      onMouseEnter={(e) => { if (value > 0) e.currentTarget.style.background = 'var(--ant-color-fill-quaternary)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
                    >
                      <Statistic title={label} value={value} valueStyle={{ color }} />
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      {/* ── Detail table ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {detailView === 'detail' ? 'Detail' : 'Summary by Team'}
        </Typography.Title>
        <Segmented
          size="small"
          value={detailView}
          onChange={setDetailView}
          options={[
            { label: 'Detail',  value: 'detail'  },
            { label: 'Summary', value: 'summary' },
          ]}
        />
      </div>
      <Table
        rowKey={(r) => detailView === 'summary'
          ? `team-${r.TeamName}`
          : `${r.StartDate}-${r.TeamName}-${r.JobName}`}
        dataSource={detailView === 'summary' ? teamRows : rows}
        // Start Date and Job carry no meaning once cohorts are combined
        columns={detailView === 'summary'
          ? summaryColumns(detailColumns)
          : detailColumns}
        loading={loading}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{ defaultPageSize: 150, showSizeChanger: true, pageSizeOptions: [50, 100, 150, 250], showTotal: (t) => `${t} record${t === 1 ? '' : 's'}` }}
        locale={{ emptyText: 'No recruitment data found for the selected filters.' }}
        summary={() => {
          if (!rows.length) return null;
          // Built with a running index rather than literals: Summary drops two
          // leading columns, and hardcoded indexes would put every total under
          // the wrong header.
          const leadSpan = detailView === 'summary' ? 1 : 3;
          let i = 0;
          const cells = [];
          const push = (node, align = 'right') => {
            cells.push(
              <Table.Summary.Cell key={i} index={i} align={align}>{node}</Table.Summary.Cell>
            );
            i += 1;
          };

          cells.push(
            <Table.Summary.Cell key="lead" index={0} colSpan={leadSpan}>
              <strong>Total</strong>
            </Table.Summary.Cell>
          );
          i = leadSpan;

          // Summary adds a # Classes column; total across teams rather than
          // distinct dates globally, since two teams starting the same day are
          // two classes
          if (detailView === 'summary') {
            push(<strong>{teamRows.reduce((s, r) => s + (r.Classes || 0), 0)}</strong>);
          }

          push(<strong>{totals.OrderCount.toFixed(1)}</strong>);
          push(<strong>{fmtFillPct(totals.OrderFillFTENumerator, totals.OrderCount)}</strong>);
          push(<strong>{totals.GraduationTargetFTE.toFixed(1)}</strong>);
          push(<strong>{totals.GradFTE.toFixed(1)}</strong>);
          push(<strong>{totalGrads}</strong>);
          push(<strong>{totals.StillEmployed}</strong>);
          push(<strong>{totalGradPct}</strong>);
          push(<strong>{fmtFillPct(totalGrads, totals.FormerTrainee + totals.InProduction + totals.FormerEmployee + totals.InterTransfer)}</strong>);
          push((() => {
            if (!totals.GraduationTargetFTE) return <strong>—</strong>;
            const val = sumGradsAboveTarget(rows);
            const color = val > 0 ? '#52c41a' : val < 0 ? '#ff4d4f' : undefined;
            return <strong style={{ color }}>{val > 0 ? `+${val.toFixed(1)}` : val.toFixed(1)}</strong>;
          })());
          push(<RetentionCell row={totals} />);
          push(<strong>{totals.AcceptedOffer}</strong>);
          push(<strong>{totals.RescindedOffer}</strong>);
          push(<strong>{totals.NoShow}</strong>);
          push(<strong>{totals.OrientationFallout}</strong>);
          push(<strong>{totals.InTraining}</strong>);
          push(<strong>{totals.FormerTrainee}</strong>);
          push(<strong>{totals.InProduction}</strong>);
          push(<strong>{totals.FormerEmployee}</strong>);
          push(<strong>{totals.InterTransfer}</strong>);
          push(<strong>{totals.Terms0to6}</strong>);
          push(<strong>{totals.Terms7to13}</strong>);
          push(<strong>{totals.Terms14to20}</strong>);
          push(<strong>{totals.Terms21to60}</strong>);
          push(<strong>{totals.Terms61to180}</strong>);
          push(<strong>{totals.Terms181plus}</strong>);
          push(<strong>{totals.Terms0to6 + totals.Terms7to13 + totals.Terms14to20 + totals.Terms21to60 + totals.Terms61to180 + totals.Terms181plus}</strong>);

          return (
            <Table.Summary fixed>
              <Table.Summary.Row>{cells}</Table.Summary.Row>
            </Table.Summary>
          );
        }}
      />

      {/* ── Non-Grads Training Cost Impact ── */}
      {!isClient && (<>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24, marginBottom: 8 }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          Non-Grads Training Cost Impact
        </Typography.Title>
        <Segmented
          size="small"
          value={costView}
          onChange={setCostView}
          options={[{ label: 'Summary', value: 'summary' }, { label: 'Detail', value: 'detail' }]}
        />
      </div>
      <Table
        rowKey={(r) => costView === 'summary'
          ? `team-${r.TeamName}`
          : `${r.StartDate}-${r.TeamName}-${r.JobName}`}
        dataSource={costView === 'summary' ? costTeamRows : costRows}
        columns={costView === 'summary' ? summaryColumns(costColumns) : costColumns}
        loading={costLoading}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => costView === 'summary' ? `${t} team${t === 1 ? '' : 's'}` : `${t} class${t === 1 ? '' : 'es'}` }}
        locale={{ emptyText: 'No non-grad cost data for the selected filters.' }}
        summary={() => {
          if (!costRows.length) return null;
          const leadSpan = costView === 'summary' ? 1 : 3;
          let i = leadSpan;
          const cells = [
            <Table.Summary.Cell key="lead" index={0} colSpan={leadSpan}>
              <strong>Total</strong>
            </Table.Summary.Cell>,
          ];
          const push = (node) => {
            cells.push(
              <Table.Summary.Cell key={i} index={i} align="right">{node}</Table.Summary.Cell>
            );
            i += 1;
          };
          if (costView === 'summary') {
            push(<strong>{costTeamRows.reduce((s, r) => s + (r.Classes || 0), 0)}</strong>);
          }
          push(<strong>{costTotals.NonGradCount}</strong>);
          push(<strong>{costTotals.TotalHours.toFixed(1)}</strong>);
          push(<strong>{fmtCurrency(costTotals.NonGradsWages)}</strong>);
          push(<strong>{fmtCurrency(costTotals.NonGradsHiringCosts)}</strong>);
          push(<strong>{fmtCurrency(costTotals.NonGradsTotalCost)}</strong>);
          return (
            <Table.Summary fixed>
              <Table.Summary.Row>{cells}</Table.Summary.Row>
            </Table.Summary>
          );
        }}
      />

      </>)}

      {/* ── Training Return ── */}
      {canViewFinancials >= 1 && !isClient && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24, marginBottom: 8 }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              Training Return
            </Typography.Title>
            <Segmented
              size="small"
              value={returnView}
              onChange={setReturnView}
              options={[{ label: 'Summary', value: 'summary' }, { label: 'Detail', value: 'detail' }]}
            />
          </div>
          <Table
            rowKey={(r) => returnView === 'summary'
              ? `team-${r.TeamName}`
              : `${r.StartDate}-${r.TeamName}-${r.JobName}`}
            dataSource={returnView === 'summary' ? returnTeamRows : returnRows}
            columns={returnView === 'summary' ? summaryColumns(returnColumns) : returnColumns}
            loading={returnLoading}
            size="small"
            scroll={{ x: 'max-content' }}
            pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => returnView === 'summary' ? `${t} team${t === 1 ? '' : 's'}` : `${t} class${t === 1 ? '' : 'es'}` }}
            locale={{ emptyText: 'No training return data for the selected filters.' }}
            summary={() => {
              if (!returnRows.length) return null;
              const leadSpan = returnView === 'summary' ? 1 : 3;
              let i = leadSpan;
              const cells = [
                <Table.Summary.Cell key="lead" index={0} colSpan={leadSpan}>
                  <strong>Total</strong>
                </Table.Summary.Cell>,
              ];
              const push = (node) => {
                cells.push(
                  <Table.Summary.Cell key={i} index={i} align="right">{node}</Table.Summary.Cell>
                );
                i += 1;
              };
              if (returnView === 'summary') {
                push(<strong>{returnTeamRows.reduce((s, r) => s + (r.Classes || 0), 0)}</strong>);
              }
              push(<strong>{returnTotals.TotalInClass}</strong>);
              push(<strong>{returnTotals.StartedOrientation}</strong>);
              push(<strong>{returnTotals.GradCount}</strong>);
              // Grads Billable After exists only in Detail; it is a per-cohort
              // day number and is hidden once cohorts are combined
              if (returnView === 'detail') push(null);
              push(<strong>{returnTotals.TrainingHours.toFixed(1)}</strong>);
              push(<strong>{returnTotals.BillableHours.toFixed(1)}</strong>);
              push(<strong>{fmtCurrency(returnTotals.TrainingCost)}</strong>);
              // Skipped, not dashed: the column itself is absent below
              // Management and these cells are positional.
              if (canViewFinancials >= 2) {
                push(<strong>{fmtCurrency(returnTotals.BillableRevenue)}</strong>);
              }
              push((() => {
                const v = returnTotals.NetReturn;
                if (canViewFinancials === 1) {
                  const color = v > 0 ? '#52c41a' : '#ff4d4f';
                  return <strong style={{ color }}>{v > 0 ? 'Positive' : 'Negative'}</strong>;
                }
                if (canViewFinancials >= 2) {
                  const color = v > 0 ? '#52c41a' : v < 0 ? '#ff4d4f' : undefined;
                  return <strong style={{ color }}>{v > 0 ? `+${fmtCurrency(v)}` : fmtCurrency(v)}</strong>;
                }
                return <span style={{ color: '#aaa' }}>—</span>;
              })());
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>{cells}</Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </>
      )}

      {/* ── Pivot table ── */}
      <Typography.Title level={5} style={{ marginTop: 24, marginBottom: 8 }}>
        Grad Stats by Team / Job / Month
      </Typography.Title>
      <Table
        rowKey="key"
        dataSource={pivot.data}
        columns={pivotColumns}
        loading={loading}
        size="small"
        scroll={{ x: 'max-content', y: 600 }}
        sticky
        pagination={false}
        locale={{ emptyText: 'No data.' }}
        style={{ marginBottom: 24 }}
        summary={() => pivot.months.length > 0 && (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={2}><strong>Total</strong></Table.Summary.Cell>
              {pivot.months.map((m, i) => {
                const t = pivot.monthTotals[m];
                return (
                  <Table.Summary.Cell key={m} index={i + 2} align="center">
                    <div style={{ lineHeight: 1.4 }}>
                      <div style={{ fontSize: 11, color: '#888' }}>Ord FTE: {t.orderCount.toFixed(1)}</div>
                      <div style={{ fontSize: 11, color: '#888' }}>Target: {t.gradTargetFTE.toFixed(1)}</div>
                      <div><strong>{t.grads}</strong></div>
                      <div style={{ fontSize: 11, color: '#888' }}>{fmtFillPct(t.grads, t.orientationFallout + t.formerTrainee + t.grads)}</div>
                    </div>
                  </Table.Summary.Cell>
                );
              })}
              {(() => {
                const grand = pivot.months.reduce((acc, m) => ({
                  orderCount:         acc.orderCount         + (pivot.monthTotals[m]?.orderCount         || 0),
                  gradTargetFTE:      acc.gradTargetFTE      + (pivot.monthTotals[m]?.gradTargetFTE      || 0),
                  orientationFallout: acc.orientationFallout + (pivot.monthTotals[m]?.orientationFallout || 0),
                  inTraining:         acc.inTraining         + (pivot.monthTotals[m]?.inTraining         || 0),
                  grads:              acc.grads              + (pivot.monthTotals[m]?.grads              || 0),
                  formerTrainee:      acc.formerTrainee      + (pivot.monthTotals[m]?.formerTrainee       || 0),
                }), { orderCount: 0, gradTargetFTE: 0, orientationFallout: 0, inTraining: 0, grads: 0, formerTrainee: 0 });
                return (
                  <Table.Summary.Cell index={pivot.months.length + 2} align="center">
                    <div style={{ lineHeight: 1.4 }}>
                      <div style={{ fontSize: 11, color: '#888' }}>Ord FTE: {grand.orderCount.toFixed(1)}</div>
                      <div style={{ fontSize: 11, color: '#888' }}>Target: {grand.gradTargetFTE.toFixed(1)}</div>
                      <div><strong>{grand.grads}</strong></div>
                      <div style={{ fontSize: 11, color: '#888' }}>{fmtFillPct(grand.grads, grand.orientationFallout + grand.formerTrainee + grand.grads)}</div>
                    </div>
                  </Table.Summary.Cell>
                );
              })()}
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />

      <Drawer
        title={`Term Detail — ${termDrawer?.label ?? ''}`}
        open={!!termDrawer}
        onClose={() => setTermDrawer(null)}
        width={1100}
        destroyOnClose
      >
        <Table
          size="small"
          loading={termDrawerLoading}
          dataSource={termDrawerRows}
          rowKey="UserID"
          pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} employee${t === 1 ? '' : 's'}` }}
          scroll={{ x: 'max-content' }}
          columns={[
            { title: 'Employee',       dataIndex: 'EmployeeName',      key: 'EmployeeName',      width: 160, ellipsis: true,
              sorter: (a, b) => (a.EmployeeName ?? '').localeCompare(b.EmployeeName ?? '') },
            { title: 'Team',           dataIndex: 'TeamName',          key: 'TeamName',          width: 110, ellipsis: true,
              sorter: (a, b) => (a.TeamName ?? '').localeCompare(b.TeamName ?? '') },
            { title: 'Job',            dataIndex: 'JobName',           key: 'JobName',           width: 90, ellipsis: true,
              render: (v) => v || '—' },
            { title: 'Class Start',    dataIndex: 'ClassStartDate',    key: 'ClassStartDate',    width: 100,
              sorter: (a, b) => (a.ClassStartDate ?? '').localeCompare(b.ClassStartDate ?? '') },
            { title: 'Eff. Hire',      dataIndex: 'EffectiveHireDate', key: 'EffectiveHireDate', width: 100,
              sorter: (a, b) => (a.EffectiveHireDate ?? '').localeCompare(b.EffectiveHireDate ?? '') },
            { title: 'Term Date',      dataIndex: 'TermDate',          key: 'TermDate',          width: 100,
              sorter: (a, b) => (a.TermDate ?? '').localeCompare(b.TermDate ?? '') },
            { title: 'Days',           dataIndex: 'DaysEmployed',      key: 'DaysEmployed',      width: 60, align: 'right',
              sorter: (a, b) => (a.DaysEmployed ?? 0) - (b.DaysEmployed ?? 0) },
            { title: 'Status',         dataIndex: 'TermStatus',        key: 'TermStatus',        width: 110,
              render: (v) => {
                const color = v?.toLowerCase() === 'terminated' ? 'error'
                            : v?.toLowerCase() === 'resigned'   ? 'warning'
                            : 'default';
                return <Tag color={color}>{v}</Tag>;
              }},
            { title: 'Term Reason',    dataIndex: 'TermReason',        key: 'TermReason',        width: 180, ellipsis: true,
              render: (v) => v || '—' },
          ]}
          locale={{ emptyText: 'No records found.' }}
        />
      </Drawer>
    </>
  );
}
